/**
 * yeti-viewer.js — Maus-reaktiver 3D-Viewer für die Section „Yeti Hero (3D)".
 *
 * - Three.js 0.170.0 (gepinnt) via Importmap von jsDelivr, kein Bundling.
 * - Die Importmap wird STATISCH von sections/yeti-hero.liquid ausgegeben —
 *   zur Laufzeit eingefügte Importmaps verwerfen Chrome/Firefox („import
 *   map added after module script load was triggered"). Hier nur Guard.
 * - Multi-Instanz: initialisiert alle [data-yeti-viewer]-Container,
 *   Config kommt als JSON aus data-yeti-config.
 * - Lazy-Loading: Three.js + GLB laden erst kurz vor Sichtbarkeit
 *   (IntersectionObserver, rootMargin 200px).
 * - Rendern pausiert offscreen (zweiter Observer, threshold 0) und bei
 *   verstecktem Tab (document.visibilitychange).
 * - Touch-Geräte (hover: none): keine Maus-Verfolgung, stattdessen sanfte
 *   sinusförmige Eigenbewegung des Kopfes.
 * - prefers-reduced-motion: Poster bleibt statisch; mit static_on_reduce
 *   wird das Modell einmal ohne Bewegung gerendert und bei Resize erneuert.
 *   Mit idle_on_reduce läuft die ruhige Idle trotzdem weiter (sonst wirkt
 *   Bartly wie eine Statue); automatische Gesten und die Touch-Eigenbewegung
 *   bleiben dann aus, Gesten nur auf Klick.
 *
 * Debug in der Konsole:
 *   __yetiViewers[0].getClipNames()
 *   __yetiViewers[0].playClip('Wave+Smile')
 *   __yetiViewers[0].getHeadBoneName()
 */

const THREE_VERSION = '0.170.0';
const CDN_BASE = `https://cdn.jsdelivr.net/npm/three@${THREE_VERSION}`;
const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
/* Effektiv «still»? Viewer mit idle_on_reduce bleiben bei Reduced Motion in Bewegung. */
const reducesFor = (config, reduce) => reduce && config.idle_on_reduce !== true;

/* Clip-Rollen (Track-Trennung ist im Modell garantiert):
   Gesten = Körper-Bones, Smile/Laugh = reine Gesichts-Clips. */
const FACE_CLIPS = ['Smile', 'Laugh'];

/* Winkel-Limits (rad), werden mit der Intensität aus der Config multipliziert. */
const HEAD_LIMIT = { yaw: 0.6, pitch: 0.35 };
const EYE_LIMIT = { yaw: 0.3, pitch: 0.18 };

/* Crossfade-Dauer Idle <-> Gesten-Clip (Spec: 0.3–0.4 s). */
const CROSSFADE = 0.35;

/* ------------------------------------------------------------------ */
/* Importmap-Guard + dynamisches Three.js-Loading (einmalig pro Seite) */
/* ------------------------------------------------------------------ */

let threePromise = null;

function loadThree() {
  /* Die Importmap kommt statisch aus sections/yeti-hero.liquid. Fehlt
     sie, kann 'three' nicht aufgelöst werden -> Init abbrechen,
     Poster stehen lassen. */
  if (document.querySelectorAll('script[type="importmap"]').length === 0) {
    console.error('[yeti-viewer] Importmap fehlt');
    return Promise.reject(new Error('Importmap fehlt'));
  }
  if (!threePromise) {
    threePromise = Promise.all([
      import('three'),
      import('three/addons/loaders/GLTFLoader.js'),
      import('three/addons/loaders/DRACOLoader.js'),
      import('three/addons/environments/RoomEnvironment.js')
    ])
      .then(([THREE, gltf, draco, room]) => ({
        THREE,
        GLTFLoader: gltf.GLTFLoader,
        DRACOLoader: draco.DRACOLoader,
        RoomEnvironment: room.RoomEnvironment
      }))
      .catch((err) => {
        /* Fehlschlag -> Retry beim nächsten Versuch erlauben. */
        threePromise = null;
        throw err;
      });
  }
  return threePromise;
}

/* ------------------------------------------------------------------ */
/* Viewer-Instanz                                                      */
/* ------------------------------------------------------------------ */

class YetiViewer {
  constructor(container, config) {
    this.container = container;
    this.config = Object.assign({
      framing: 'head_cutout',
      zoom: 1,
      focus_y: 0,
      model_rotation: 0,
      head_intensity: 1,
      eye_intensity: 0.5,
      head_bone_name: '',
      eye_bone_names: '',
      trigger_clip: 'Wave',
      trigger_event: 'click',
      trigger_interval: 15,
      static_on_reduce: false,
      idle_on_reduce: false,
      /* Lukas 12.10.2026: das 3D-Modell wirkte neben dem 2D-Bartly flach und emotionslos. */
      studio_light: false,     /* weiches Umgebungslicht + Koralle-/Kühl-Kantenlicht statt Hemisphere/Directional */
      expression: '',          /* 'smile': geschlossenes Lächeln wie im Logo (Mundwinkel hoch, dunkle Lächel-Linie) */
      expression_strength: 4.5,
      expression_jaw: 0.4,
      mouth_color: '#141c3c'
    }, config);

    this.isTouch = window.matchMedia('(hover: none)').matches;
    this.motionReduced = reducesFor(this.config, motionPreference.matches);

    /* Maus-Verfolgung: Ziel- und aktuelle (geglättete) Winkel. */
    this.headTarget = { yaw: 0, pitch: 0 };
    this.headCurrent = { yaw: 0, pitch: 0 };
    this.eyeTarget = { yaw: 0, pitch: 0 };
    this.eyeCurrent = { yaw: 0, pitch: 0 };

    /* Bones + Basis-Pose (initiale lokale Quaternionen, kein Akkumulieren). */
    this.headBone = null;
    this.eyeBones = [];
    this.baseQuats = new Map();       /* bone.uuid -> Quaternion */
    this.drivenBones = new Set();     /* Bone-Namen aktiver Clip-Tracks */

    /* Generations-Token pro Face-Clip (Race-Guard fürs Stop-Timeout). */
    this.faceClipTokens = new Map();  /* clip.name -> Zähler */
    /* Trigger-Cooldown (click/hover/auto): true, solange noch ein
       Body-Trigger-Clip läuft — verhindert Fade-Pops durch Re-Trigger.
       Reine Face-Trigger (Smile/Laugh) sperren nicht. */
    this.triggerBusy = false;
    this.manualRemaining = 0;

    /* Gecachte Quaternionen/Achsen für applyOffset (keine Allokationen
       pro Frame), werden in setup() nach dem THREE-Load initialisiert. */
    this._qWorld = null;
    this._qParent = null;
    this._qLocal = null;
    this._qTmp = null;
    this._axisY = null;
    this._axisX = null;

    this.initialized = false;
    this.ready = false;
    this.disposed = false;
    this.onScreen = false;
    this.pageVisible = !document.hidden;
  }

  /* --- Debug-API ----------------------------------------------------- */
  getClipNames() {
    return (this.clips || []).map((c) => c.name);
  }

  getHeadBoneName() {
    return this.headBone ? this.headBone.name : null;
  }

  /* --- Init (lädt Three.js + GLB) ------------------------------------ */
  async init() {
    if (this.initialized || this.disposed) return;
    this.initialized = true;
    try {
      await this.setup();
      /* Dispose während des asynchronen Setups (shopify:section:unload):
         still aussteigen — KEIN Fehlerwurf, damit der Boot-Observer
         keinen init()-Retry auslöst. */
      if (this.disposed) return;
    } catch (err) {
      /* Fehlschlag (GLB-404, Netzfehler, fehlende Importmap):
         partiell erzeugten Renderer freigeben und Flag zurücksetzen,
         damit der nächste Viewport-Eintritt / section:load erneut
         versuchen kann. Poster bleibt stehen. */
      if (this.renderer) {
        this.renderer.setAnimationLoop(null);
        this.renderer.dispose();
        this.renderer = null;
      }
      this.initialized = false;
      this.ready = false;
      throw err;
    }
  }

  async setup() {
    const { THREE, GLTFLoader, DRACOLoader, RoomEnvironment } = await loadThree();
    /* Dispose-Race: section:unload während des Three.js-Loads ->
       still abbrechen (dispose() hat bereits aufgeräumt). */
    if (this.disposed) return;
    this.THREE = THREE;

    /* Frame-Caches für applyOffset initialisieren. */
    this._qWorld = new THREE.Quaternion();
    this._qParent = new THREE.Quaternion();
    this._qLocal = new THREE.Quaternion();
    this._qTmp = new THREE.Quaternion();
    this._axisY = new THREE.Vector3(0, 1, 0);
    this._axisX = new THREE.Vector3(1, 0, 0);

    const canvas = this.container.querySelector('canvas');
    this.renderer = new THREE.WebGLRenderer({
      canvas: canvas || undefined,
      alpha: true,
      antialias: true
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);

    if (this.config.studio_light === true) {
      this.setupStudioLight(THREE, RoomEnvironment);
    } else {
      /* Licht: Hemisphere + Directional */
      this.scene.add(new THREE.HemisphereLight(0xffffff, 0x33415c, 1.1));
      const dir = new THREE.DirectionalLight(0xffffff, 1.6);
      dir.position.set(2.5, 4, 3);
      this.scene.add(dir);
    }

    /* GLB ist Draco-komprimiert -> Dekoder von jsDelivr. */
    const dracoLoader = new DRACOLoader();
    this.dracoLoader = dracoLoader; /* Referenz für dispose() merken. */
    dracoLoader.setDecoderPath(`${CDN_BASE}/examples/jsm/libs/draco/gltf/`);
    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    const gltf = await loader.loadAsync(this.config.model_url);
    /* Dispose-Race: section:unload während des GLB-Loads -> dispose()
       hat Renderer/DracoLoader bereits freigegeben, still aussteigen,
       bevor Szene/AnimationLoop aufgebaut werden (Editor-Zombie). */
    if (this.disposed) return;

    /* Model-Root (für model_rotation um Y). */
    this.modelRoot = new THREE.Group();
    this.modelRoot.add(gltf.scene);
    this.modelRoot.rotation.y = THREE.MathUtils.degToRad(
      Number(this.config.model_rotation) || 0
    );
    this.scene.add(this.modelRoot);
    this.model = gltf.scene;

    this.clips = gltf.animations || [];
    this.mixer = new THREE.AnimationMixer(this.model);
    this.mixer.addEventListener('finished', (e) => this.onClipFinished(e.action));

    this.setupBones();
    this.setupIdle();
    this.setupExpression();
    this.setupCamera();
    this.setupResize();
    this.setupPointer();
    this.setupTriggers();
    this.setupVisibility();

    this.clock = new THREE.Clock();
    this.ready = true;
    this.renderer.setAnimationLoop(this.motionReduced ? null : () => this.tick());
    if (this.motionReduced && this.config.static_on_reduce === true) this.renderStill();
    /* Das Poster bleibt bis zum ersten erfolgreichen Frame in tick()/renderStill()
       sichtbar — auch wenn der Sichtbarkeits-Observer noch nicht bereit ist. */
  }

  /* --- Bone-Findung ---------------------------------------------------- */
  findBone(exactNames, fallbackKey) {
    let found = null;
    this.model.traverse((o) => {
      if (found || !o.isBone) return;
      if (exactNames.indexOf(o.name) !== -1) found = o;
    });
    if (!found && fallbackKey) {
      const key = fallbackKey.toLowerCase();
      this.model.traverse((o) => {
        if (found || !o.isBone) return;
        if (o.name.toLowerCase().indexOf(key) !== -1) found = o;
      });
    }
    return found;
  }

  setupBones() {
    const cfg = this.config;
    const manualHead = String(cfg.head_bone_name || '').trim();

    /* Manuelle Namen aus der Config überschreiben die Auto-Findung. */
    this.headBone = manualHead
      ? this.findBone([manualHead], null)
      : this.findBone(['DEF-head'], 'head');

    if (!this.headBone) {
      console.warn('[yeti-viewer] Kein Head-Bone gefunden');
      /* Rest läuft weiter, nur Kopf-/Augen-Tracking ist aus. */
      return;
    }

    /* Augen: manuelle Liste (Komma-getrennt) oder Auto DEF-eye.L/R. */
    const eyeNames = String(cfg.eye_bone_names || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const eyeList = eyeNames.length
      ? eyeNames.map((n) => this.findBone([n], null)).filter(Boolean)
      : [
          this.findBone(['DEF-eye.L'], 'eye.l'),
          this.findBone(['DEF-eye.R'], 'eye.r')
        ].filter(Boolean);

    /* Augen-Intensität 0 = Augen aus. */
    this.eyeBones = Number(cfg.eye_intensity) > 0 ? eyeList : [];

    /* Basis-Pose speichern (initiale lokale Quaternionen). */
    [this.headBone].concat(this.eyeBones).forEach((b) => {
      this.baseQuats.set(b.uuid, b.quaternion.clone());
    });
  }

  /* --- Clips ------------------------------------------------------------ */
  findClip(name) {
    const n = String(name).trim().toLowerCase();
    return (
      this.clips.find((c) => c.name === name) ||
      this.clips.find((c) => c.name.toLowerCase() === n) ||
      null
    );
  }

  markDriven(action, on) {
    action.getClip().tracks.forEach((t) => {
      const bone = t.name.split('.')[0];
      if (on) this.drivenBones.add(bone);
      else this.drivenBones.delete(bone);
    });
  }

  /* Studio-Licht wie in den Blender-Renderings: weiche Umgebung (gibt dem Fell Tiefe),
     warmes Hauptlicht vorne links, Koralle-Kante hinten rechts, kühle Kante hinten links. */
  setupStudioLight(THREE, RoomEnvironment) {
    this.renderer.toneMapping = THREE.NeutralToneMapping || THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.95;
    if (RoomEnvironment) {
      const pmrem = new THREE.PMREMGenerator(this.renderer);
      this.envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      pmrem.dispose();
      this.scene.environment = this.envTexture;
      this.scene.environmentIntensity = 0.3;
    }
    this.scene.add(new THREE.HemisphereLight(0xdfeeff, 0x16323e, 0.35));
    const light = (color, intensity, x, y, z) => {
      const l = new THREE.DirectionalLight(color, intensity);
      l.position.set(x, y, z);
      this.scene.add(l);
    };
    light(0xfff2e6, 2.2, -2.2, 3.5, 3.2);
    light(0xff6a4d, 2.6, 2.6, 2.4, -2.8);
    light(0x8fc8ff, 1.6, -2.8, 3.0, -2.6);
  }

  /* Dauerhafter Ausdruck wie im 2D-Logo: geschlossenes Lächeln mit dunkler Lächel-Linie.
     Lukas 12.10.2026: der offene Mund mit roten Zähnen sah schlimm aus. Darum nur die
     Mundwinkel aus dem Smile-Clip (additiv, verstärkt), der Kiefer nur einen Spalt offen,
     und das Mundinnere dunkelblau wie die Logo-Linie. Brauen und Augenlider bleiben frei. */
  setupExpression() {
    if (this.config.expression !== 'smile') return;
    const THREE = this.THREE;
    const src = this.findClip('Smile');
    if (!src || !THREE.AnimationUtils || !THREE.AdditiveAnimationBlendMode) return;
    const hold = (pattern, weight, name) => {
      const tracks = src.tracks.filter((t) => pattern.test(t.name)).map((t) => t.clone());
      if (!tracks.length || !(weight > 0)) return null;
      const clip = new THREE.AnimationClip(name, src.duration, tracks);
      THREE.AnimationUtils.makeClipAdditive(clip, 0);
      const action = this.mixer.clipAction(clip);
      action.blendMode = THREE.AdditiveAnimationBlendMode;
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;
      action.play();
      action.time = src.duration * 0.5;
      action.paused = true;
      action.setEffectiveWeight(weight);
      return action;
    };
    this.expressionAction = hold(/cheek/i, Number(this.config.expression_strength) || 4.5, 'BartlyMundwinkel');
    this.expressionJaw = hold(/jaw/i, Number(this.config.expression_jaw) || 0.4, 'BartlyKieferSpalt');
    const mouth = new THREE.Color(this.config.mouth_color || '#141c3c');
    this.model.traverse((o) => {
      if (!o.isMesh) return;
      (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => {
        if (!m || ['gusi', 'gigi', 'lidah'].indexOf(m.name) === -1) return;
        m.color.copy(mouth);
        m.map = null;
        m.roughness = 0.75;
        m.metalness = 0;
        if (m.emissive) m.emissive.setRGB(0, 0, 0);
        m.needsUpdate = true;
      });
    });
  }

  setupIdle() {
    let idleClip = this.findClip('Idle');
    if (!idleClip && this.clips.length) {
      /* Fallback auf den ersten Clip — sichtbar machen, weil
         trigger_clip='Idle' dann eine Kollision erzeugen würde. */
      idleClip = this.clips[0];
      console.warn(
        '[yeti-viewer] Kein "Idle"-Clip gefunden, nutze "' + idleClip.name + '"'
      );
    }
    if (!idleClip) return;
    this.idleAction = this.mixer.clipAction(idleClip);
    this.idleAction.setLoop(this.THREE.LoopRepeat, Infinity);
    this.idleAction.play();
    /* Idle läuft dauerhaft -> ihre Tracks treiben Bones permanent. */
    this.markDriven(this.idleAction, true);
  }

  /* Kombo-Syntax: "Wave+Smile". Körper-Clips faden die Idle aus,
     Gesichts-Clips (Smile/Laugh) laufen oben drauf (Idle bleibt an). */
  playClip(spec, weight = 1, explicit = false) {
    if (!this.mixer || (this.motionReduced && !explicit)) return false;
    let played = false;
    String(spec || '')
      .split('+')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((name) => {
        const clip = this.findClip(name);
        if (!clip) {
          console.warn('[yeti-viewer] Clip nicht gefunden:', name);
          return;
        }
        if (FACE_CLIPS.indexOf(clip.name) !== -1) {
          this.playFaceClip(clip, weight);
          played = true;
        } else if (this.idleAction && clip === this.idleAction.getClip()) {
          /* Trigger zeigt auf die Idle: playBodyClip würde DIESELBE
             gecachte Action auf LoopOnce/clampWhenFinished umkonfigurieren
             und sich selbst ausfaden -> Idle friert am Endframe ein. */
          console.warn('[yeti-viewer] Trigger-Clip ist die Idle — ignoriert');
        } else {
          this.playBodyClip(clip, weight);
          played = true;
        }
      });
    return played;
  }

  /* A deliberate button press permits one gesture, never ambient motion. */
  playOnce(spec) {
    if (!this.ready || this.disposed || this.triggerBusy || this.manualRemaining > 0) return false;
    if (!this.playClip(spec, 1, true)) return false;
    if (this.motionReduced) {
      const durations = String(spec).split('+').map((name) => this.findClip(name.trim()))
        .filter(Boolean).map((clip) => clip.duration);
      this.manualRemaining = Math.max(...durations) + CROSSFADE + 0.1;
      if (this.idleAction) this.idleAction.paused = true;
      this.container.dataset.yetiGesture = 'once';
      this.clock.getDelta();
      this.renderer.setAnimationLoop(() => this.tick());
    }
    return true;
  }

  finishOneShot() {
    this.manualRemaining = 0;
    this.mixer.stopAllAction();
    this.currentBodyAction = null;
    if (this.faceActions) this.faceActions.clear();
    this.triggerBusy = false;
    if (this.idleAction) {
      this.idleAction.reset().setEffectiveWeight(1).play();
      this.mixer.update(0);
      this.idleAction.paused = true;
    }
    this.container.dataset.yetiGesture = 'still';
    this.renderer.setAnimationLoop(null);
    this.renderStill();
  }

  playBodyClip(clip, weight) {
    const THREE = this.THREE;
    /* Laufende Geste sauber ausblenden. */
    if (this.currentBodyAction) {
      this.currentBodyAction.fadeOut(0.3);
      this.markDriven(this.currentBodyAction, false);
      this.currentBodyAction = null;
    }
    const action = this.mixer.clipAction(clip);
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.setEffectiveWeight(weight);
    action.fadeIn(0.3);
    action.play();
    this.currentBodyAction = action;
    this.markDriven(action, true);
    /* Cooldown für click/hover/auto bis onClipFinished (Fade-Pops). */
    this.triggerBusy = true;

    /* Idle ausfaden (Crossfade 0.3–0.4 s). */
    if (this.idleAction) this.idleAction.fadeOut(CROSSFADE);
  }

  playFaceClip(clip, weight) {
    const THREE = this.THREE;
    /* Generations-Token: mixer.clipAction() liefert für denselben Clip
       dasselbe gecachte Action-Objekt. Wird der Clip erneut getriggert,
       bevor das Stop-Timeout (350 ms) eines früheren Durchlaufs feuert,
       darf das alte Timeout die neue Wiedergabe nicht killen. */
    const token = (this.faceClipTokens.get(clip.name) || 0) + 1;
    this.faceClipTokens.set(clip.name, token);
    const action = this.mixer.clipAction(clip);
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.setEffectiveWeight(weight);
    action.fadeIn(0.3);
    action.play();
    /* Idle NICHT ausfaden — Gesicht läuft oben drauf. */
    this.faceActions = this.faceActions || new Set();
    this.faceActions.add(action);
    this.markDriven(action, true);
  }

  onClipFinished(action) {
    if (action === this.currentBodyAction) {
      /* Zurück zur Idle crossfaden. */
      action.fadeOut(CROSSFADE);
      this.markDriven(action, false);
      this.currentBodyAction = null;
      if (this.idleAction) {
        this.idleAction.enabled = true;
        this.idleAction.paused = false;
        this.idleAction.fadeIn(CROSSFADE);
      }
    } else if (this.faceActions && this.faceActions.has(action)) {
      /* Gesichts-Clip sanft ausblenden, Idle läuft darunter weiter. */
      action.fadeOut(0.3);
      this.faceActions.delete(action);
      const clip = action.getClip();
      const token = this.faceClipTokens.get(clip.name);
      setTimeout(() => {
        /* Race-Guard: Clip wurde seitdem erneut getriggert (neues Token)
           -> die laufende Wiedergabe NICHT stoppen. */
        if (this.disposed) return;
        if (this.faceClipTokens.get(clip.name) !== token) return;
        clip.tracks.forEach((t) => this.drivenBones.delete(t.name.split('.')[0]));
        action.stop();
      }, 350);
    }
    /* Trigger-Cooldown freigeben, sobald kein Body-Clip mehr läuft
       (Face-Clips sperren den Cooldown nicht). */
    if (!this.currentBodyAction) {
      this.triggerBusy = false;
    }
  }

  /* --- Kamera ------------------------------------------------------------ */
  setupCamera() {
    const THREE = this.THREE;
    const cfg = this.config;
    this.scene.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(this.model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;

    const target = new THREE.Vector3();
    let baseDist;

    if (cfg.framing === 'head_cutout' && this.headBone) {
      /* Kamera zielt auf die Weltposition des Kopf-Bones,
         der Körper wird unten abgeschnitten. */
      this.headBone.getWorldPosition(target);
      baseDist = size.y * 0.7;
    } else {
      /* Ganze Figur via Box3-Bounding einpassen. */
      target.copy(center);
      const fov = THREE.MathUtils.degToRad(this.camera.fov);
      baseDist = (maxDim / 2 / Math.tan(fov / 2)) * 1.3;
    }

    /* focus_y verschiebt das Ziel vertikal (in % der Modellhöhe). */
    target.y += ((Number(cfg.focus_y) || 0) / 100) * size.y;

    /* zoom multipliziert die Distanz. */
    const dist = baseDist * (Number(cfg.zoom) || 1);

    this.camera.position.set(target.x, target.y, target.z + dist);
    this.camera.lookAt(target);
    this.resize();
  }

  setupResize() {
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);
    this.resize();
  }

  resize() {
    if (!this.renderer) return;
    const w = this.container.clientWidth || 1;
    const h = this.container.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this.motionReduced && this.config.static_on_reduce === true) this.renderStill();
  }

  /* --- Maus-Verfolgung / Touch-Eigenbewegung ----------------------------- */
  setupPointer() {
    if (this.isTouch) return; /* Touch: sinusförmige Eigenbewegung im Tick. */
    this.onPointerMove = (e) => {
      if (this.motionReduced) return;
      const r = this.container.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const nx = Math.max(
        -1,
        Math.min(1, (e.clientX - (r.left + r.width / 2)) / (r.width / 2))
      );
      const ny = Math.max(
        -1,
        Math.min(1, (e.clientY - (r.top + r.height / 2)) / (r.height / 2))
      );
      const hi = Number(this.config.head_intensity) || 0;
      const ei = Number(this.config.eye_intensity) || 0;
      this.headTarget.yaw = nx * HEAD_LIMIT.yaw * hi;
      this.headTarget.pitch = ny * HEAD_LIMIT.pitch * hi;
      this.eyeTarget.yaw = nx * EYE_LIMIT.yaw * ei;
      this.eyeTarget.pitch = ny * EYE_LIMIT.pitch * ei;
    };
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
  }

  /* --- Auslöser ------------------------------------------------------------ */
  setupTriggers() {
    const ev = this.config.trigger_event;
    const clip = this.config.trigger_clip;
    if (!ev || ev === 'none' || !clip) return;

    /* Cooldown gegen Fade-Pops: triggerBusy wird gesetzt, sobald ein
       Body-Clip startet, und in onClipFinished wieder freigegeben.
       Alle drei Trigger-Pfade (click/hover/auto) prüfen dasselbe Flag. */
    if (ev === 'click') {
      this.onClick = () => {
        if (this.triggerBusy) return;
        this.playClip(clip);
      };
      this.container.addEventListener('click', this.onClick);
    } else if (ev === 'hover') {
      if (this.isTouch) return; /* Hover nicht auf Touch-Geräten. */
      this.onPointerEnter = () => {
        if (this.triggerBusy) return;
        this.playClip(clip);
      };
      this.container.addEventListener('pointerenter', this.onPointerEnter);
    } else if (ev === 'auto') {
      const sec = Math.max(1, Number(this.config.trigger_interval) || 15);
      this.autoInterval = setInterval(() => {
        /* Keine automatischen Gesten, wenn das System Bewegung reduziert. */
        if (this.triggerBusy || !this.onScreen || !this.pageVisible || motionPreference.matches) return;
        this.playClip(clip);
      }, sec * 1000);
    }
  }

  /* --- Pausieren: offscreen + Tab-Wechsel ---------------------------------- */
  setReducedMotion(reduce) {
    if (this.manualRemaining > 0 && this.mixer && this.renderer) this.finishOneShot();
    this.motionReduced = reduce;
    if (this.bootObserver && !this.initialized && !this.disposed) {
      this.bootObserver.disconnect();
      if (!reduce || this.config.static_on_reduce === true) this.bootObserver.observe(this.container);
    }
    if (!this.renderer || !this.clock || this.disposed) return;
    /* Eine bereits geladene Figur bleibt als ruhiges Standbild stehen.
       Beim Fortsetzen wird die verstrichene Pausenzeit nicht animiert. */
    if (!reduce) {
      if (this.idleAction) this.idleAction.paused = false;
      this.clock.getDelta();
    }
    this.renderer.setAnimationLoop(reduce ? null : () => this.tick());
    if (reduce && this.config.static_on_reduce === true) this.renderStill();
  }

  setupVisibility() {
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          this.onScreen = en.isIntersecting;
        });
      },
      { threshold: 0 }
    );
    this.visibilityObserver.observe(this.container);
    this.onVisibilityChange = () => {
      this.pageVisible = !document.hidden;
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /* --- Render-Loop ---------------------------------------------------------- */
  renderStill() {
    if (!this.ready || this.disposed || !this.renderer) return;
    /* Erstes Standbild: Idle-Pose anwenden, ohne die Animation zeitlich
       voranzutreiben. Spätere Resize-Frames behalten die eingefrorene Pose. */
    if (!this.frameRendered) this.mixer.update(0);
    this.renderer.render(this.scene, this.camera);
    if (!this.frameRendered) {
      this.frameRendered = true;
      this.container.classList.add('is-loaded');
    }
  }

  tick() {
    /* Dispose-Race: Instanz bereits abgeräumt (Editor-Zombie). */
    if (this.disposed || (this.motionReduced && this.manualRemaining <= 0)) return;
    /* Pausiert: offscreen oder Tab versteckt. */
    if (!this.onScreen || !this.pageVisible || document.hidden) return;

    const dt = Math.min(this.clock.getDelta(), 0.1);
    if (this.motionReduced) {
      this.mixer.update(dt);
      this.renderer.render(this.scene, this.camera);
      this.manualRemaining -= dt;
      if (this.manualRemaining <= 0) this.finishOneShot();
      return;
    }
    const t = this.clock.elapsedTime;

    /* Touch-Geräte: sanfte sinusförmige Eigenbewegung des Kopfes
       (nicht bei Reduced Motion, dort bleibt nur die Idle). */
    if (this.isTouch && !motionPreference.matches) {
      const hi = Number(this.config.head_intensity) || 0;
      const ei = Number(this.config.eye_intensity) || 0;
      this.headTarget.yaw = Math.sin(t * 0.5) * HEAD_LIMIT.yaw * 0.35 * hi;
      this.headTarget.pitch = Math.sin(t * 0.33) * HEAD_LIMIT.pitch * 0.3 * hi;
      this.eyeTarget.yaw = Math.sin(t * 0.5 + 0.4) * EYE_LIMIT.yaw * 0.3 * ei;
      this.eyeTarget.pitch = Math.sin(t * 0.33 + 0.7) * EYE_LIMIT.pitch * 0.3 * ei;
    }

    /* Exponentielle Glättung: current += (target-current)*(1-exp(-dt*k)). */
    const kHead = 6;
    const kEyes = 8;
    const sH = 1 - Math.exp(-dt * kHead);
    const sE = 1 - Math.exp(-dt * kEyes);
    this.headCurrent.yaw += (this.headTarget.yaw - this.headCurrent.yaw) * sH;
    this.headCurrent.pitch += (this.headTarget.pitch - this.headCurrent.pitch) * sH;
    this.eyeCurrent.yaw += (this.eyeTarget.yaw - this.eyeCurrent.yaw) * sE;
    this.eyeCurrent.pitch += (this.eyeTarget.pitch - this.eyeCurrent.pitch) * sE;

    this.mixer.update(dt);

    /* Welche Bones schreibt der Mixer DIESES Frame wirklich? Nur Actions
       mit enabled=true beschreiben ihre Tracks. Nach einem fadeOut ist
       enabled=false — dann schreibt der Mixer den Bone NICHT mehr und
       unser Offset würde sich auf die eigene letzte Pose akkumulieren
       ("Zombie-Kopf"). Deshalb pro Frame frisch bestimmen. */
    const written = new Set();
    const collect = (action) => {
      if (!action || !action.enabled) return;
      action.getClip().tracks.forEach((tr) => written.add(tr.name.split('.')[0]));
    };
    collect(this.idleAction);
    collect(this.currentBodyAction);
    if (this.faceActions) this.faceActions.forEach(collect);
    this.mixerWritten = written;

    /* Bone-Offsets NACH mixer.update() in Welt-Raum anwenden. */
    if (this.headBone) this.applyOffset(this.headBone, this.headCurrent);
    this.eyeBones.forEach((b) => this.applyOffset(b, this.eyeCurrent));

    this.renderer.render(this.scene, this.camera);

    /* Erster erfolgreicher Frame -> Poster sanft ausfaden. */
    if (!this.frameRendered) {
      this.frameRendered = true;
      this.container.classList.add('is-loaded');
    }
  }

  applyOffset(bone, angles) {
    /* Basis-Pose restoren, wenn der Mixer den Bone DIESES Frame nicht
       geschrieben hat (sonst akkumuliert sich unser Offset auf die eigene
       Vorframe-Pose). Geschriebene Bones starten frisch vom Mixer-Stand. */
    if (!this.mixerWritten || !this.mixerWritten.has(bone.name)) {
      const base = this.baseQuats.get(bone.uuid);
      if (base) bone.quaternion.copy(base);
    }
    if (!angles.yaw && !angles.pitch) return;

    /* qWorldOffset aus Yaw/Pitch bauen (gecachte Instanz-Quaternionen,
       keine Allokationen pro Frame) ... */
    const qWorld = this._qWorld
      .setFromAxisAngle(this._axisY, angles.yaw)
      .multiply(this._qTmp.setFromAxisAngle(this._axisX, angles.pitch));
    /* ... in den Parent-Raum konvertieren ... */
    const qParent = bone.parent.getWorldQuaternion(this._qParent);
    const qLocal = this._qLocal
      .copy(qParent)
      .invert()
      .multiply(qWorld)
      .multiply(qParent);
    /* ... und auf die Post-Mixer-Pose multiplizieren. */
    bone.quaternion.premultiply(qLocal);
  }

  /* --- Aufräumen (Theme-Editor: shopify:section:unload) ----------------- */
  dispose() {
    this.disposed = true;
    this.ready = false;

    if (this.envTexture) {
      this.envTexture.dispose();
      this.envTexture = null;
    }
    /* Render-Loop + GPU-Ressourcen. */
    if (this.renderer) {
      this.renderer.setAnimationLoop(null);
      this.renderer.forceContextLoss();
      this.renderer.dispose();
      this.renderer = null;
    }
    /* Mixer + laufende Clips stoppen. */
    if (this.mixer) {
      this.mixer.stopAllAction();
      this.mixer = null;
    }
    /* GPU-Speicher des Modells freigeben (Geometrien, Texturen,
       Materialien) — sonst Memory-Leak bei Editor-Re-Loads. */
    if (this.model) {
      this.model.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
          (Array.isArray(o.material) ? o.material : [o.material]).forEach(
            (m) => {
              for (const k in m) {
                const v = m[k];
                if (v && v.isTexture) v.dispose();
              }
              m.dispose();
            }
          );
        }
      });
      this.model = null;
      this.modelRoot = null;
    }
    /* Draco-Dekoder (WASM/Worker) freigeben. */
    if (this.dracoLoader) {
      this.dracoLoader.dispose();
      this.dracoLoader = null;
    }
    /* Observer disconnecten. */
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    if (this.bootObserver) {
      this.bootObserver.disconnect();
      this.bootObserver = null;
    }
    /* Auto-Trigger stoppen. */
    if (this.autoInterval) {
      clearInterval(this.autoInterval);
      this.autoInterval = null;
    }
    /* Event-Listener entfernen (Referenzen wurden in setup* gespeichert). */
    if (this.onPointerMove) {
      window.removeEventListener('pointermove', this.onPointerMove);
      this.onPointerMove = null;
    }
    if (this.onClick) {
      this.container.removeEventListener('click', this.onClick);
      this.onClick = null;
    }
    if (this.onPointerEnter) {
      this.container.removeEventListener('pointerenter', this.onPointerEnter);
      this.onPointerEnter = null;
    }
    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.onVisibilityChange = null;
    }

    /* Aus der Registry entfernen. */
    const arr = window.__yetiViewers || [];
    const i = arr.indexOf(this);
    if (i !== -1) arr.splice(i, 1);

    /* Flags zurücksetzen, damit section:load sauber neu initialisiert. */
    delete this.container.dataset.yetiWired;
    this.initialized = false;
  }
}

/* ------------------------------------------------------------------ */
/* Boot: Multi-Instanz + Lazy-Loading                                  */
/* ------------------------------------------------------------------ */

window.__yetiViewers = window.__yetiViewers || [];

function bootYetiViewers() {
  document.querySelectorAll('[data-yeti-viewer]').forEach((container) => {
    if (container.dataset.yetiWired === '1') return;

    let config = {};
    try {
      config = JSON.parse(container.getAttribute('data-yeti-config') || '{}');
    } catch (err) {
      console.error('[yeti-viewer]', err);
      return;
    }
    if (!config.model_url) return;
    /* Nur ausdrücklich konfigurierte Hero-Modelle dürfen bei Reduced
       Motion ein ruhiges 3D-Standbild laden. Andere behalten ihr Poster. */
    if (reducesFor(config, motionPreference.matches) && config.static_on_reduce !== true) return;
    container.dataset.yetiWired = '1';

    const viewer = new YetiViewer(container, config);
    window.__yetiViewers.push(viewer);

    /* Lazy: Three.js + GLB erst laden, wenn der Container kurz vor
       Sichtbarkeit ist (~200px vorher). */
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting || (viewer.motionReduced && viewer.config.static_on_reduce !== true)) return;
          viewer
            .init()
            .then(() => io.disconnect())
            .catch((err) => {
              /* Ladefehler: Poster stehen lassen, Observer bleibt aktiv
                 -> Retry beim nächsten Viewport-Eintritt. */
              console.error('[yeti-viewer]', err);
            });
        });
      },
      { rootMargin: '200px 0px' }
    );
    viewer.bootObserver = io;
    io.observe(container);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootYetiViewers);
} else {
  bootYetiViewers();
}
document.addEventListener('shopify:section:load', bootYetiViewers);

/* Änderungen der Systemeinstellung gelten auch ohne Neuladen der Seite. */
motionPreference.addEventListener('change', (event) => {
  (window.__yetiViewers || []).forEach((viewer) => {
    viewer.setReducedMotion(reducesFor(viewer.config, event.matches));
  });
  if (!event.matches) bootYetiViewers();
});

/* Theme-Editor: Section wird entladen -> betroffene Viewer-Instanzen
   aufräumen, damit das folgende section:load sauber neu initialisiert. */
document.addEventListener('shopify:section:unload', (e) => {
  const root = e.target;
  if (!root || typeof root.contains !== 'function') return;
  (window.__yetiViewers || []).slice().forEach((viewer) => {
    if (root.contains(viewer.container)) viewer.dispose();
  });
});
